/**
 * GET /umed/kanban (backend/src/services/umed/kanban.js): the Board layout's
 * one read. Types mirror the server's; nothing here decides a column.
 */
import { api } from '../../services/api';
import type { ApiResult, LegendEntry, UmedCase, ActionType, CreationReason, UmedType, Stage } from './umedApi';
import type { KanbanChip } from '../../components/board/kanban/types';
import type { ResubmittalBoardPage } from '../finance/resubmittalsApi';

export type UmedColumnKey = 'needs-creation' | 'packet-ready' | 'submitted' | 'ready-to-approve' | 'approved-finance-pending' | 'with-finance' | 'resubmittals';
// Not 'lead-closed' (2026-10-14): closed_on is NULL on every open case. 'waiting' = sent to DCF, else submitted.
export type UmedKanbanSort = 'resident' | 'facility' | 'waiting' | 'created' | 'submitted' | 'sent-to-dcf' | 'approved';
export type UmedKanbanOwner = 'mine' | 'unassigned';

export interface UmedKanbanCard {
  id: string;
  patient_id: string | null;
  column: UmedColumnKey;
  rank: number;
  first_name: string | null;
  last_name: string | null;
  facility_name: string | null;
  /** 2026-10-14: no building -> the resident's CounselEAR Preferred Clinic (not a confirmed building). */
  counselear_clinic_id?: string | null;
  counselear_clinic_name?: string | null;
  umed_type: UmedType | null;
  purpose: CreationReason | null;
  stage: Stage;
  status: { word: string | null; legend: LegendEntry | null; source: 'EMERGE' | 'MONDAY' | 'ADOPTED' | null };
  effective: { state: string; label: string };
  next_action: { action_type: ActionType; due_on: string | null } | null;
  due: 'OVERDUE' | 'TODAY' | 'UPCOMING' | null;
  attention: string[];
  owner: UmedCase['owner'];
  claimable: boolean;
  chips: KanbanChip[];
  /**
   * The open correction (2026-10-29): its kind, whether it went, and the note for Gwynn asked with it -- null where none
   * was written, withheld (`note_withheld`) where it names NOCA or FLMMIS for a caller who may not read those. Absent on a
   * payload from before.
   */
  correction?: { correction_type: string; state: 'REQUESTED' | 'SUBMITTED'; note: string | null; note_withheld?: boolean } | null;
  other_open_case: boolean;
  approval: { canonical: 'PROVEN' | 'NOT_PROVEN'; ready_to_approve: boolean; in_approval_scope: boolean };
  /** Facts a drop needs, never the capability: the screen ANDs them with scope.can, the server decides again. */
  acts: { choose_sending: boolean; approve: boolean };
  updated_at: string | null;
}

export interface UmedKanbanColumn {
  key: UmedColumnKey;
  label: string;
  what: string;
  count: number;
  page: number;
  limit: number;
  has_more: boolean;
  cards: UmedKanbanCard[];
}

export interface UmedElsewhere {
  key: string;
  label: string;
  what: string;
  count: number;
  /** The board tab it is worked in, or the exception kind of My work. */
  tab: string | null;
  exception_kind: string | null;
}

export interface UmedKanbanPage {
  board: 'umed';
  q: string;
  owner: UmedKanbanOwner | null;
  sort: UmedKanbanSort | null;
  dir: 'asc' | 'desc';
  sorts: UmedKanbanSort[];
  owners: UmedKanbanOwner[];
  columns: UmedKanbanColumn[];
  /** Absent on a one-column page. */
  elsewhere?: UmedElsewhere[];
  total?: number;
  elsewhere_total?: number;
  /** What an Elsewhere number is: cases placed by this board's precedence; the tab it opens counts residents. */
  elsewhere_basis?: string;
  column?: UmedColumnKey;
}

export interface UmedKanbanQuery {
  q?: string;
  owner?: UmedKanbanOwner | null;
  sort?: UmedKanbanSort | null;
  dir?: 'asc' | 'desc' | null;
  column?: UmedColumnKey | null;
  page?: number;
  limit?: number;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * GET /umed/resubmittals (backend routes/umedWorkspace.js): the DCF resubmittals board Finance works, read in UMED's own
 * scope. Read-only here: Finance starts and hands over (UMED starts one from the case's status menu, 2026-10-19); UMED
 * works a resubmittal from the case.
 */
export const umedResubmittalsApi = {
  board: async (p: { q?: string; column?: string; page?: number; limit?: number }, signal?: AbortSignal): Promise<ApiResult<ResubmittalBoardPage>> => {
    const url = `/umed/resubmittals${qs({ q: p.q, column: p.column, page: p.page && p.page > 1 ? p.page : null, limit: p.limit })}`;
    return (await (signal ? api.get(url, undefined, { signal }) : api.get(url))) as ApiResult<ResubmittalBoardPage>;
  },
};

export const umedKanbanApi = {
  read: async (l: UmedKanbanQuery, signal?: AbortSignal): Promise<ApiResult<UmedKanbanPage>> => {
    const url = `/umed/kanban${qs({ q: l.q, owner: l.owner, sort: l.sort, dir: l.sort ? l.dir : null, column: l.column, page: l.page && l.page > 1 ? l.page : null, limit: l.limit })}`;
    return (await (signal ? api.get(url, undefined, { signal }) : api.get(url))) as ApiResult<UmedKanbanPage>;
  },
};
